import React, { useCallback, useEffect, useState } from 'react';
import { authHeaders } from './Admin';

import { API_BASE } from '../apiBase';

const C = {
  panel: '#141416',
  panelAlt: '#1a1a1d',
  border: '#262629',
  text: '#f2f2f3',
  sub: '#8a8a90',
  accent: '#2D9E5F',
};

const FROM = 'usman@gardenoai.com';

interface EmailBatch {
  batch_id: string;
  from: string;
  subject: string;
  body: string;
  recipients: string[];
  sent: number;
  failed: number;
  error: string;
  sent_at: string;
}

function when(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
    timeZone: 'Asia/Karachi',
  });
}

// Same split the server does, so the count under the field matches what sends.
function countRecipients(raw: string): number {
  return new Set(raw.split(/[,;\s]+/).map((s) => s.trim().toLowerCase()).filter(Boolean)).size;
}

const input: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box', padding: '11px 14px', borderRadius: 10,
  border: `1px solid ${C.border}`, background: C.panelAlt, color: C.text, fontSize: 14, fontFamily: 'inherit',
};

function EmailTab({ token, isMobile, onUnauthorized }: { token: string; isMobile: boolean; onUnauthorized: () => void }) {
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; msg: string } | null>(null);

  const [rows, setRows] = useState<EmailBatch[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/admin/emails?limit=100`, { headers: authHeaders(token) });
      if (res.status === 401) { onUnauthorized(); return; }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const d = await res.json();
      setRows(((d.data ?? d).emails ?? []) as EmailBatch[]);
    } catch (e: any) {
      setError(e.message || 'Failed to load');
    }
    setLoading(false);
  }, [token, onUnauthorized]);

  useEffect(() => { load(); }, [load]);

  const n = countRecipients(to);

  async function send() {
    if (!to.trim() || !subject.trim() || !body.trim()) {
      setResult({ ok: false, msg: 'Recipients, subject and message are all required.' });
      return;
    }
    // Email can't be unsent, so a multi-recipient send asks once more.
    if (n > 1 && !confirming) { setConfirming(true); return; }
    setConfirming(false);
    setSending(true);
    setResult(null);
    try {
      const res = await fetch(`${API_BASE}/admin/emails`, {
        method: 'POST',
        headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
        body: JSON.stringify({ to, subject: subject.trim(), body: body.trim() }),
      });
      if (res.status === 401) { onUnauthorized(); return; }
      const d = await res.json().catch(() => ({}));
      if (!res.ok) {
        setResult({ ok: false, msg: d?.error?.message || `Failed (${res.status}).` });
      } else {
        const r = d.data ?? d;
        setResult({ ok: true, msg: `Sent to ${r.sent} recipient${r.sent === 1 ? '' : 's'}.` });
        setTo(''); setSubject(''); setBody('');
      }
      // Failed sends are logged too, so refresh either way.
      load();
    } catch {
      setResult({ ok: false, msg: 'Could not reach the server.' });
    } finally {
      setSending(false);
    }
  }

  // Prefill the composer from an old email, to resend or follow up.
  function reuse(b: EmailBatch) {
    setTo(b.recipients.join(', '));
    setSubject(b.subject);
    setBody(b.body);
    setResult(null);
    setConfirming(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  const th: React.CSSProperties = {
    textAlign: 'left', padding: '11px 12px', fontSize: 11.5, color: C.sub,
    fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4,
  };
  const td: React.CSSProperties = {
    textAlign: 'left', padding: '11px 12px', fontSize: 13.5, color: C.text,
    borderTop: `1px solid ${C.border}`, verticalAlign: 'top',
  };

  return (
    <>
      <div style={{ maxWidth: 640, marginTop: 26, background: C.panel, border: `1px solid ${C.border}`, borderRadius: 16, padding: isMobile ? 18 : 24 }}>
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Send an email</div>
        <div style={{ fontSize: 12, color: C.sub, marginBottom: 18 }}>
          From <span style={{ color: C.text }}>{FROM}</span>. Replies come back to the same inbox. Each recipient gets their own copy and can't see the others.
        </div>

        <div style={{ fontSize: 13, color: C.sub, marginBottom: 6 }}>To</div>
        <textarea value={to} onChange={(e) => { setTo(e.target.value); setConfirming(false); }} rows={2}
          placeholder="name@example.com, another@example.com"
          style={{ ...input, resize: 'vertical' }} />
        <div style={{ fontSize: 12, color: n > 100 ? '#F87171' : C.sub, margin: '6px 0 14px' }}>
          {n === 0 ? 'Separate addresses with commas, spaces or new lines.' : `${n} recipient${n === 1 ? '' : 's'}${n > 100 ? ' (max 100 per send)' : ''}`}
        </div>

        <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" maxLength={200}
          style={{ ...input, marginBottom: 12 }} />
        <textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Write your message…" rows={10} maxLength={20000}
          style={{ ...input, resize: 'vertical', lineHeight: 1.55 }} />
        <div style={{ fontSize: 12, color: C.sub, marginTop: 6 }}>Plain text. A blank line starts a new paragraph.</div>

        <button onClick={send} disabled={sending || n > 100}
          style={{ marginTop: 16, width: '100%', background: confirming ? '#B45309' : C.accent, color: '#fff', border: 'none', borderRadius: 12, padding: '13px', fontWeight: 700, fontSize: 15, cursor: 'pointer', opacity: sending || n > 100 ? 0.6 : 1 }}>
          {sending ? 'Sending…' : confirming ? `Confirm: send to ${n} people` : n > 1 ? `Send to ${n} recipients` : 'Send'}
        </button>
        {confirming && (
          <button onClick={() => setConfirming(false)}
            style={{ marginTop: 8, width: '100%', background: 'transparent', color: C.sub, border: `1px solid ${C.border}`, borderRadius: 12, padding: '10px', fontSize: 14, cursor: 'pointer' }}>
            Cancel
          </button>
        )}
        {result && <p style={{ marginTop: 14, fontSize: 14, color: result.ok ? '#4ADE80' : '#F87171' }}>{result.msg}</p>}
      </div>

      <div style={{ marginTop: 32 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
          <h2 style={{ fontSize: 17, fontWeight: 800, margin: 0, color: C.text }}>Sent emails</h2>
          <button onClick={load}
            style={{ marginLeft: 'auto', background: C.panelAlt, border: `1px solid ${C.border}`, borderRadius: 8, padding: '7px 14px', cursor: 'pointer', color: C.text, fontSize: 13 }}>
            Refresh
          </button>
        </div>
        <p style={{ color: C.sub, fontSize: 12.5, margin: '0 0 12px' }}>
          Every email sent from this panel, newest first. Click a row to read the full message.
        </p>

        {error && <div style={{ color: '#F87171', fontSize: 13.5, marginBottom: 10 }}>{error}</div>}
        {loading && rows.length === 0 && <div style={{ color: C.sub, fontSize: 13.5 }}>Loading…</div>}

        <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 14, overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 640 }}>
            <thead>
              <tr>
                <th style={th}>Sent</th>
                <th style={th}>Email</th>
                <th style={th}>To</th>
                <th style={{ ...th, textAlign: 'right' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((b) => {
                const expanded = open === b.batch_id;
                return (
                  <tr key={b.batch_id} onClick={() => setOpen(expanded ? null : b.batch_id)} style={{ cursor: 'pointer' }}>
                    <td style={{ ...td, whiteSpace: 'nowrap', color: C.sub, fontSize: 12.5 }}>{when(b.sent_at)}</td>
                    <td style={{ ...td, maxWidth: 420 }}>
                      <div style={{ fontWeight: 700 }}>{b.subject}</div>
                      <div style={{
                        fontSize: 12.5, color: C.sub, lineHeight: 1.5, whiteSpace: expanded ? 'pre-wrap' : 'normal',
                        ...(expanded ? {} : { overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' as const }),
                      }}>{b.body}</div>
                      {expanded && (
                        <button onClick={(e) => { e.stopPropagation(); reuse(b); }}
                          style={{ marginTop: 10, background: C.panelAlt, border: `1px solid ${C.border}`, borderRadius: 8, padding: '5px 12px', cursor: 'pointer', color: C.text, fontSize: 12.5 }}>
                          Use as draft
                        </button>
                      )}
                    </td>
                    <td style={{ ...td, fontSize: 12.5, wordBreak: 'break-all' }}>
                      {expanded || b.recipients.length <= 3
                        ? b.recipients.join(', ')
                        : `${b.recipients.slice(0, 3).join(', ')} +${b.recipients.length - 3}`}
                    </td>
                    <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {b.failed === 0
                        ? <span style={{ color: '#34D399', fontWeight: 700 }}>Sent</span>
                        : <span style={{ color: '#F87171', fontWeight: 700 }} title={b.error}>Failed</span>}
                      {expanded && b.error && <div style={{ fontSize: 11.5, color: '#F87171', marginTop: 4, whiteSpace: 'normal', maxWidth: 220 }}>{b.error}</div>}
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && !loading && (
                <tr><td colSpan={4} style={{ ...td, textAlign: 'center', color: C.sub }}>No emails sent yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

export default EmailTab;
